import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { inventoryReturnPath } from './inventoryRouteState';
import OrgModuleLayout from './OrgModuleLayout';
import OrgModulePlaceholder from './OrgModulePlaceholder';
import Drawer from '@app/components/Drawer';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import { useApiConfiguration } from '@app/hooks/useApiConfiguration';
import { useDevicesApi } from '@app/hooks/useApis';
import { ResponseError } from '@app/types/sdk';
import type { Device, DeviceRunningConfigSnapshot, DeviceRunningConfigVersion } from '@app/types/sdk';
import { ApiResponseError, buildOrgUrl, cloneHeadersWithAuth } from '@app/utils/api';
import { formatDateTime, formatNumber } from '@app/utils/format';
import { CopyIcon, DownloadIcon, EyeIcon, ForkIcon } from '@app/components/icons';
import type { DeviceCVEExposure } from '../CVEGlobalSummaryPage';


const DeviceRunningConfigPage = () => {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnToInventory = inventoryReturnPath((location.state as { from?: unknown } | null)?.from);
  const orgId = useActiveOrgId();
  const apiConfig = useApiConfiguration();
  const devicesApi = useDevicesApi();
  const basePath = apiConfig.basePath ?? '';

  const [versions, setVersions] = useState<DeviceRunningConfigVersion[]>([]);
  const [latest, setLatest] = useState<DeviceRunningConfigSnapshot | null>(null);
  const [device, setDevice] = useState<Device | null>(null);
  const [diffText, setDiffText] = useState<string | null>(null);
  const [startupDiffText, setStartupDiffText] = useState<string | null>(null);
  const [startupConfigText, setStartupConfigText] = useState<string | null>(null);
  const [selectedFrom, setSelectedFrom] = useState<string>('');
  const [selectedTo, setSelectedTo] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [diffLoading, setDiffLoading] = useState(false);
  const [startupDiffLoading, setStartupDiffLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSample] = useState(false);
  const [versionsLimit, setVersionsLimit] = useState(5);
  const [activeSnapshotId, setActiveSnapshotId] = useState<string | null>(null);
  const [lineCounts, setLineCounts] = useState<Record<string, number>>({});
  const [diffMode, setDiffMode] = useState<'unified' | 'side-by-side' | 'word'>('unified');
  const [snapshotDrawerId, setSnapshotDrawerId] = useState<string | null>(null);
  const [snapshotDrawerLoading, setSnapshotDrawerLoading] = useState(false);
  const [snapshotDrawerSnapshot, setSnapshotDrawerSnapshot] = useState<DeviceRunningConfigSnapshot | null>(null);
  const [startupDiffDrawerOpen, setStartupDiffDrawerOpen] = useState(false);

  const [assessments, setAssessments] = useState<DeviceCVEExposure[]>([]);
  const [assessmentsLoading, setAssessmentsLoading] = useState(false);
  const [selectedExposure, setSelectedExposure] = useState<DeviceCVEExposure | null>(null);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);


  const isDev = import.meta.env.MODE !== 'production';
  const sampleDeviceId = deviceId ?? 'sample-device';
  const sampleBaseDate = useMemo(() => new Date(), []);
  const sampleVersions = useMemo<DeviceRunningConfigVersion[]>(
    () => [
      {
        id: 'sample-2026-01-29',
        deviceId: sampleDeviceId,
        collectedAt: new Date(sampleBaseDate.getTime()),
        hash: 'sample-hash-2026-01-29',
        sizeBytes: 1380,
        diffSummary: ['Added VLAN 30 to trunk allowed list.', 'Enabled OSPF for 10.10.0.0/16.']
      },
      {
        id: 'sample-2026-01-28',
        deviceId: sampleDeviceId,
        collectedAt: new Date(sampleBaseDate.getTime() - 24 * 60 * 60 * 1000),
        hash: 'sample-hash-2026-01-28',
        sizeBytes: 1164,
        diffSummary: ['Baseline snapshot created.']
      }
    ],
    [sampleBaseDate, sampleDeviceId]
  );
  const sampleDiffText = useMemo(
    () =>
      [
        '--- running-config (2026-01-28)',
        '+++ running-config (2026-01-29)',
        '@@ interface Gi0/1 @@',
        '- switchport trunk allowed vlan 10,20',
        '+ switchport trunk allowed vlan 10,20,30',
        '@@ router ospf 1 @@',
        '+ network 10.10.0.0 0.0.255.255 area 0'
      ].join('\n'),
    []
  );
  const sampleLatest = useMemo<DeviceRunningConfigSnapshot>(
    () => ({
      id: 'sample-2026-01-29',
      deviceId: sampleDeviceId,
      collectedAt: new Date(sampleBaseDate.getTime()),
      sizeBytes: 1380,
      hash: 'sample-hash-2026-01-29',
      startupConfigInSync: true,
      configSummary: ['Device acting as edge layer with OSPF enabled.', 'Trunk uplink includes VLAN 30.', 'SSH access enabled.'],
      text: [
        'hostname edge-lab-1',
        '!',
        'interface Gi0/1',
        ' description Uplink-to-core',
        ' switchport mode trunk',
        ' switchport trunk allowed vlan 10,20,30',
        '!',
        'router ospf 1',
        ' network 10.10.0.0 0.0.255.255 area 0',
        '!',
        'line vty 0 4',
        ' login local',
        ' transport input ssh'
      ].join('\n')
    }),
    [sampleBaseDate, sampleDeviceId]
  );
  const sampleSnapshots = useMemo<DeviceRunningConfigSnapshot[]>(() => {
    const latest = sampleLatest;
    const older: DeviceRunningConfigSnapshot = {
      id: 'sample-2026-01-28',
      deviceId: sampleDeviceId,
      collectedAt: new Date(sampleBaseDate.getTime() - 24 * 60 * 60 * 1000),
      sizeBytes: 1164,
      hash: 'sample-hash-2026-01-28',
      text: [
        'hostname edge-lab-1',
        '!',
        'interface Gi0/1',
        ' description Uplink-to-core',
        ' switchport mode trunk',
        ' switchport trunk allowed vlan 10,20',
        '!',
        'router ospf 1',
        '!',
        'line vty 0 4',
        ' login local',
        ' transport input ssh'
      ].join('\n')
    };
    return [latest, older];
  }, [sampleBaseDate, sampleDeviceId, sampleLatest]);

  const resolveErrorMessage = useCallback((caught: unknown, fallback: string, scope: 'load' | 'diff') => {
    if (caught instanceof ResponseError) {
      const status = caught.response?.status;
      if (status === 404) {
        return scope === 'diff' ? 'Selected snapshots were not found.' : 'No running config snapshots found yet.';
      }
      if (status === 501) {
        return 'Running config service is not enabled yet.';
      }
    }
    return fallback;
  }, []);

  const isSampleMode = useMemo(() => {
    return isDev && showSample && !loading && !latest?.text && versions.length === 0;
  }, [isDev, latest?.text, loading, showSample, versions.length]);

  const copyToClipboard = useCallback(async (text: string, label: string) => {
    if (!text) {
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : `${label} copy failed.`;
      setError(message);
    }
  }, []);

  const downloadText = useCallback((text: string, filename: string) => {
    if (!text) {
      return;
    }
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(url);
  }, []);

  const effectiveLatest = useMemo(() => {
    if (isSampleMode) {
      return sampleLatest;
    }
    return latest;
  }, [isSampleMode, latest, sampleLatest]);

  const effectiveVersions = useMemo(() => {
    if (isSampleMode) {
      return sampleVersions;
    }
    return versions;
  }, [isSampleMode, sampleVersions, versions]);

  const compactDiffText = useCallback((raw: string) => {
    if (!raw.trim()) {
      return raw;
    }
    const lines = raw.split('\n');
    const addedCounts = new Map<string, number>();
    const removedCounts = new Map<string, number>();
    const normalize = (value: string) => value.trim();
    const isNoise = (value: string) => {
      if (!value) {
        return true;
      }
      if (value === '!') {
        return true;
      }
      if (value.startsWith('! ') || value.startsWith('!')) {
        return true;
      }
      if (value.startsWith('Building configuration')) {
        return true;
      }
      if (value.startsWith('Current configuration')) {
        return true;
      }
      if (value.startsWith('Last configuration change')) {
        return true;
      }
      return false;
    };

    lines.forEach((line) => {
      if (!line || (line[0] !== '+' && line[0] !== '-')) {
        return;
      }
      if (line.startsWith('+++') || line.startsWith('---')) {
        return;
      }
      const normalized = normalize(line.slice(1));
      if (isNoise(normalized)) {
        return;
      }
      const bucket = line[0] === '+' ? addedCounts : removedCounts;
      bucket.set(normalized, (bucket.get(normalized) ?? 0) + 1);
    });

    for (const [value, addCount] of addedCounts) {
      const removeCount = removedCounts.get(value) ?? 0;
      if (removeCount > 0) {
        const overlap = Math.min(addCount, removeCount);
        if (overlap === addCount) {
          addedCounts.delete(value);
        } else {
          addedCounts.set(value, addCount - overlap);
        }
        if (overlap === removeCount) {
          removedCounts.delete(value);
        } else {
          removedCounts.set(value, removeCount - overlap);
        }
      }
    }

    const keptLines: string[] = [];
    lines.forEach((line) => {
      if (!line) {
        return;
      }
      if (line.startsWith('+++') || line.startsWith('---') || line.startsWith('@@')) {
        return;
      }
      if (line[0] !== '+' && line[0] !== '-') {
        return;
      }
      const normalized = normalize(line.slice(1));
      if (isNoise(normalized)) {
        return;
      }
      if (line[0] === '+') {
        const remaining = addedCounts.get(normalized) ?? 0;
        if (remaining > 0) {
          keptLines.push(line);
          addedCounts.set(normalized, remaining - 1);
        }
        return;
      }
      const remaining = removedCounts.get(normalized) ?? 0;
      if (remaining > 0) {
        keptLines.push(line);
        removedCounts.set(normalized, remaining - 1);
      }
    });

    if (keptLines.length === 0) {
      return raw;
    }
    return keptLines.join('\n');
  }, []);

  const effectiveDiffText = useMemo(() => {
    const raw = isSampleMode ? sampleDiffText : diffText;
    if (!raw) {
      return raw;
    }
    return compactDiffText(raw);
  }, [compactDiffText, diffText, isSampleMode, sampleDiffText]);

  const effectiveStartupDiffText = useMemo(() => {
    if (!startupDiffText) {
      return startupDiffText;
    }
    return compactDiffText(startupDiffText);
  }, [compactDiffText, startupDiffText]);

  const displayDiffText = effectiveStartupDiffText ?? effectiveDiffText;

  const drawerVersion = useMemo(() => {
    if (!snapshotDrawerId) {
      return null;
    }
    return effectiveVersions.find((version) => version.id === snapshotDrawerId) ?? null;
  }, [effectiveVersions, snapshotDrawerId]);

  const drawerConfigSummary = useMemo(() => {
    const snapshotSummary = snapshotDrawerSnapshot?.configSummary;
    if (Array.isArray(snapshotSummary) && snapshotSummary.length > 0) {
      return snapshotSummary;
    }
    const summary = drawerVersion?.configSummary;
    return Array.isArray(summary) && summary.length > 0 ? summary : null;
  }, [drawerVersion?.configSummary, snapshotDrawerSnapshot]);

  const latestConfigSummary = useMemo(() => {
    const summary = effectiveLatest?.configSummary;
    return Array.isArray(summary) && summary.length > 0 ? summary : null;
  }, [effectiveLatest?.configSummary]);

  const enabledFeatures = useMemo(() => {
    return [...(device?.featuresEnabled ?? [])].sort((a, b) => a.localeCompare(b));
  }, [device?.featuresEnabled]);

  const sortedVersions = useMemo(() => {
    return [...effectiveVersions].sort((a, b) => new Date(b.collectedAt).getTime() - new Date(a.collectedAt).getTime());
  }, [effectiveVersions]);

  const historyVersions = useMemo(() => {
    return sortedVersions.slice(0, versionsLimit);
  }, [sortedVersions, versionsLimit]);

  const diffFilename = useMemo(() => {
    const id = deviceId ?? 'device';
    if (startupDiffText) {
      return `${id}-running-config-startup-diff.diff`;
    }
    const from = selectedFrom || 'from';
    const to = selectedTo || 'to';
    return `${id}-running-config-diff-${from}-to-${to}.diff`;
  }, [deviceId, selectedFrom, selectedTo, startupDiffText]);

  const drawerFilename = useMemo(() => {
    if (!snapshotDrawerSnapshot?.collectedAt) {
      return null;
    }
    const stamp = snapshotDrawerSnapshot.collectedAt.toString().replace(/[:.]/g, '-');
    const id = deviceId ?? 'device';
    return `${id}-running-config-${stamp}.txt`;
  }, [deviceId, snapshotDrawerSnapshot?.collectedAt]);

  const startupDiffFilename = useMemo(() => {
    const id = deviceId ?? 'device';
    return `${id}-running-config-startup-diff.diff`;
  }, [deviceId]);

  const startupConfigFilename = useMemo(() => {
    const id = deviceId ?? 'device';
    return `${id}-startup-config.txt`;
  }, [deviceId]);

  const historyRows = useMemo(() => {
    return historyVersions.map((version, index) => {
      const older = historyVersions[index + 1] ?? null;
      const currentLines = lineCounts[version.id];
      const olderLines = older ? lineCounts[older.id] : undefined;
      let changeValue = 1;
      let changeLabel = 'Lines changed unavailable';

      if (currentLines != null && olderLines != null) {
        changeValue = Math.abs(currentLines - olderLines);
        changeLabel = `Δ ${formatNumber(changeValue)} lines`;
      } else if (currentLines != null) {
        changeValue = currentLines;
        changeLabel = `${formatNumber(currentLines)} lines`;
      }

      return { version, changeValue, changeLabel };
    });
  }, [historyVersions, lineCounts]);

  const maxChangeValue = useMemo(() => {
    return Math.max(1, ...historyRows.map((row) => row.changeValue));
  }, [historyRows]);

  const showMoreActive = versionsLimit > 5;

  useEffect(() => {
    if (!orgId || !deviceId) {
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      setDevice(null);
      setAssessments([]);
      try {
        setAssessmentsLoading(true);
        const latestUrl = buildOrgUrl(basePath, orgId, `devices/${encodeURIComponent(deviceId)}/running-config`);
        const latestHeaders = await cloneHeadersWithAuth({
          Accept: 'application/json'
        });
        const deviceResponse = await devicesApi.orgsOrgIdDevicesDeviceIdGetRaw({ orgId, deviceId }, { cache: 'no-store' });
        const devicePayload = (await deviceResponse.raw.json()) as Device;
        if (cancelled) {
          return;
        }
        setDevice(devicePayload);
        const [versionsPayload, latestResponse] = await Promise.all([
          devicesApi.orgsOrgIdDevicesDeviceIdRunningConfigVersionsGet({
            orgId,
            deviceId,
            limit: versionsLimit
          }),
          fetch(latestUrl, {
            method: 'GET',
            headers: latestHeaders
          })
        ]);
        if (cancelled) {
          return;
        }
        if (!latestResponse.ok) {
          throw new ApiResponseError(latestResponse, `Request failed with status ${latestResponse.status}`);
        }
        const latestPayload = (await latestResponse.json()) as DeviceRunningConfigSnapshot;
        const nextVersions = (versionsPayload.items ?? []).slice().sort((a, b) => {
          return new Date(b.collectedAt).getTime() - new Date(a.collectedAt).getTime();
        });
        setVersions(nextVersions);
        setLatest(latestPayload);
        if (nextVersions.length >= 2) {
          setSelectedTo(nextVersions[0]?.id ?? '');
          setSelectedFrom(nextVersions[1]?.id ?? '');
        } else if (nextVersions.length === 1) {
          setSelectedTo(nextVersions[0]?.id ?? '');
          setSelectedFrom('');
        } else {
          setSelectedTo('');
          setSelectedFrom('');
        }

        const cvesUrl = buildOrgUrl(basePath, orgId, `cves/assessments?device_id=${encodeURIComponent(deviceId)}&limit=100`);
        let deviceAssessments: DeviceCVEExposure[] = [];
        try {
          const cvesResponse = await fetch(cvesUrl, {
            method: 'GET',
            headers: latestHeaders
          });
          if (cvesResponse.ok) {
            const cvesPayload = await cvesResponse.json() as { items: DeviceCVEExposure[] };
            deviceAssessments = cvesPayload.items ?? [];
          }
        } catch (e) {
          console.error('Failed to fetch CVE assessments for device', e);
        }
        setAssessments(deviceAssessments);
      } catch (caught) {
        if (cancelled) {
          return;
        }
        const message = resolveErrorMessage(caught, 'Failed to load running config.', 'load');
        setVersions([]);
        setLatest(null);
        setError(message);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setAssessmentsLoading(false);
        }
      }

    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [deviceId, devicesApi, orgId, versionsLimit]);

  useEffect(() => {
    if (!effectiveLatest) {
      return;
    }
    if (!activeSnapshotId || activeSnapshotId === effectiveLatest.id) {
      setActiveSnapshotId(effectiveLatest.id);
    }
  }, [activeSnapshotId, effectiveLatest]);

  useEffect(() => {
    if (!isSampleMode) {
      return;
    }
    if (!activeSnapshotId || activeSnapshotId === sampleLatest.id) {
      setActiveSnapshotId(sampleLatest.id);
    }
  }, [activeSnapshotId, isSampleMode, sampleLatest]);

  useEffect(() => {
    if (!isDev || !showSample || loading || versions.length > 0) {
      return;
    }
    if (!selectedTo || !selectedFrom) {
      setSelectedTo(sampleVersions[0]?.id ?? '');
      setSelectedFrom(sampleVersions[1]?.id ?? '');
    }
  }, [isDev, loading, sampleVersions, selectedFrom, selectedTo, showSample, versions.length]);

  useEffect(() => {
    if (isSampleMode) {
      const sampleMap: Record<string, number> = {};
      sampleSnapshots.forEach((snapshot) => {
        sampleMap[snapshot.id] = snapshot.text.split('\n').length;
      });
      setLineCounts(sampleMap);
    }
  }, [isSampleMode, sampleSnapshots]);

  useEffect(() => {
    if (isSampleMode || !orgId || !deviceId) {
      return;
    }
    const missing = historyVersions.filter((version) => lineCounts[version.id] == null);
    if (missing.length === 0) {
      return;
    }
    let cancelled = false;
    const loadLineCounts = async () => {
      try {
        const results = await Promise.all(
          missing.map(async (version) => {
            const resource = `devices/${encodeURIComponent(deviceId)}/running-config`;
            const url = `${buildOrgUrl(basePath, orgId, resource)}?snapshotId=${encodeURIComponent(version.id)}`;
            const headers = await cloneHeadersWithAuth({
              Accept: 'application/json'
            });
            const response = await fetch(url, { headers });
            if (!response.ok) {
              throw new ApiResponseError(response, `Request failed with status ${response.status}`);
            }
            const payload = (await response.json()) as DeviceRunningConfigSnapshot;
            const lines = payload.text ? payload.text.split('\n').length : 0;
            return { id: version.id, lines };
          })
        );
        if (cancelled) {
          return;
        }
        setLineCounts((prev) => {
          const next = { ...prev };
          results.forEach((result) => {
            next[result.id] = result.lines;
          });
          return next;
        });
      } catch {
        // Ignore line-count failures; UI falls back to "unavailable".
      }
    };
    void loadLineCounts();
    return () => {
      cancelled = true;
    };
  }, [basePath, deviceId, historyVersions, isSampleMode, lineCounts, orgId]);

  useEffect(() => {
    if (!orgId || !deviceId || !selectedFrom || !selectedTo || selectedFrom === selectedTo) {
      setDiffText(null);
      return;
    }
    let cancelled = false;
    const loadDiff = async () => {
      setDiffLoading(true);
      setStartupDiffText(null);
      setError(null);
      try {
        const payload = await devicesApi.orgsOrgIdDevicesDeviceIdRunningConfigDiffGet({
          orgId,
          deviceId,
          from: selectedFrom,
          to: selectedTo
        });
        if (!cancelled) {
          setDiffText(payload.diff ?? '');
        }
      } catch (caught) {
        if (!cancelled) {
          const message = resolveErrorMessage(caught, 'Failed to load diff.', 'diff');
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setDiffLoading(false);
        }
      }
    };
    void loadDiff();
    return () => {
      cancelled = true;
    };
  }, [deviceId, devicesApi, orgId, selectedFrom, selectedTo]);

  const loadSnapshotForDrawer = useCallback(
    async (snapshotId: string) => {
      if (!orgId || !deviceId) {
        return;
      }
      if (isSampleMode) {
        const match = sampleSnapshots.find((snapshot) => snapshot.id === snapshotId) ?? null;
        setSnapshotDrawerSnapshot(match);
        setSnapshotDrawerLoading(false);
        return;
      }
      setSnapshotDrawerLoading(true);
      setError(null);
      try {
        const resource = `devices/${encodeURIComponent(deviceId)}/running-config`;
        const url = `${buildOrgUrl(basePath, orgId, resource)}?snapshotId=${encodeURIComponent(snapshotId)}`;
        const headers = await cloneHeadersWithAuth({
          Accept: 'application/json'
        });
        const response = await fetch(url, { headers });
        if (!response.ok) {
          throw new ApiResponseError(response, `Request failed with status ${response.status}`);
        }
        const payload = (await response.json()) as DeviceRunningConfigSnapshot;
        setSnapshotDrawerSnapshot(payload);
      } catch (caught) {
        const message =
          caught instanceof ApiResponseError
            ? `Unable to load snapshot (${caught.response.status}).`
            : caught instanceof Error
            ? caught.message
            : 'Unable to load snapshot.';
        setError(message);
      } finally {
        setSnapshotDrawerLoading(false);
      }
    },
    [basePath, deviceId, isSampleMode, orgId, sampleSnapshots]
  );

  const handleSelectSnapshot = useCallback((snapshotId: string) => {
    setActiveSnapshotId(snapshotId);
    setStartupDiffText(null);
  }, []);

  const handleShowSnapshot = useCallback(
    (snapshotId: string) => {
      setSnapshotDrawerId(snapshotId);
      setSnapshotDrawerSnapshot(null);
      void loadSnapshotForDrawer(snapshotId);
    },
    [loadSnapshotForDrawer]
  );

  const handleLoadStartupDiff = useCallback(async () => {
    if (!orgId || !deviceId) {
      return;
    }
    if (latest?.startupConfigInSync !== false) {
      return;
    }
    setStartupDiffDrawerOpen(true);
    setStartupDiffLoading(true);
    setStartupDiffText(null);
    setStartupConfigText(null);
    setError(null);
    try {
      const headers = await cloneHeadersWithAuth({
        Accept: 'application/json'
      });
      const [diffResponse, startupConfigResponse] = await Promise.all([
        fetch(buildOrgUrl(basePath, orgId, `devices/${encodeURIComponent(deviceId)}/running-config/startup-diff`), {
          method: 'GET',
          headers
        }),
        fetch(buildOrgUrl(basePath, orgId, `devices/${encodeURIComponent(deviceId)}/running-config/startup-config`), {
          method: 'GET',
          headers
        })
      ]);
      if (!diffResponse.ok) {
        throw new ApiResponseError(diffResponse, `Request failed with status ${diffResponse.status}`);
      }
      if (!startupConfigResponse.ok) {
        throw new ApiResponseError(startupConfigResponse, `Request failed with status ${startupConfigResponse.status}`);
      }
      const diffPayload = (await diffResponse.json()) as { diff?: string };
      const startupConfigPayload = (await startupConfigResponse.json()) as { text?: string };
      setStartupDiffText(diffPayload.diff ?? '');
      setStartupConfigText(startupConfigPayload.text ?? '');
    } catch (caught) {
      const message = resolveErrorMessage(caught, 'Failed to load startup diff.', 'diff');
      setError(message);
    } finally {
      setStartupDiffLoading(false);
    }
  }, [basePath, deviceId, latest?.startupConfigInSync, orgId, resolveErrorMessage]);

  const parseUnifiedDiff = useCallback((text: string) => {
    return text.split('\n').map((line) => {
      if (line.startsWith('+++') || line.startsWith('---') || line.startsWith('@@')) {
        return { type: 'meta' as const, text: line };
      }
      if (line.startsWith('+')) {
        return { type: 'add' as const, text: line };
      }
      if (line.startsWith('-')) {
        return { type: 'del' as const, text: line };
      }
      return { type: 'context' as const, text: line };
    });
  }, []);

  const renderUnifiedDiff = useCallback(
    (text: string) => {
      const lines = parseUnifiedDiff(text);
      return lines.map((line, index) => {
        if (line.type === 'meta') {
          return (
            <div key={`${index}-${line.text}`} className="running-config__diff-line running-config__diff-line--meta">
              {line.text}
            </div>
          );
        }
        if (line.type === 'add') {
          return (
            <div key={`${index}-${line.text}`} className="running-config__diff-line running-config__diff-line--added">
              {line.text}
            </div>
          );
        }
        if (line.type === 'del') {
          return (
            <div key={`${index}-${line.text}`} className="running-config__diff-line running-config__diff-line--removed">
              {line.text}
            </div>
          );
        }
        return (
          <div key={`${index}-${line.text}`} className="running-config__diff-line">
            {line.text || ' '}
          </div>
        );
      });
    },
    [parseUnifiedDiff]
  );

  const buildSideBySideRows = useCallback((text: string) => {
    const lines = parseUnifiedDiff(text);
    const rows: Array<{
      meta?: string;
      left?: string;
      right?: string;
      leftType?: 'context' | 'del';
      rightType?: 'context' | 'add';
    }> = [];
    let i = 0;
    while (i < lines.length) {
      const line = lines[i];
      if (line.type === 'meta') {
        rows.push({ meta: line.text });
        i += 1;
        continue;
      }
      if (line.type === 'context') {
        rows.push({ left: line.text, right: line.text, leftType: 'context', rightType: 'context' });
        i += 1;
        continue;
      }
      if (line.type === 'del') {
        const deletions: string[] = [];
        const additions: string[] = [];
        while (i < lines.length && lines[i].type === 'del') {
          deletions.push(lines[i].text);
          i += 1;
        }
        while (i < lines.length && lines[i].type === 'add') {
          additions.push(lines[i].text);
          i += 1;
        }
        const max = Math.max(deletions.length, additions.length);
        for (let idx = 0; idx < max; idx += 1) {
          const left = deletions[idx];
          const right = additions[idx];
          rows.push({
            left,
            right,
            leftType: left ? 'del' : undefined,
            rightType: right ? 'add' : undefined
          });
        }
        continue;
      }
      if (line.type === 'add') {
        rows.push({ right: line.text, rightType: 'add' });
        i += 1;
        continue;
      }
      i += 1;
    }
    return rows;
  }, [parseUnifiedDiff]);

  const tokenize = useCallback((value: string) => {
    return value.split(/(\s+)/).filter((token) => token.length > 0);
  }, []);

  const diffTokens = useCallback((leftTokens: string[], rightTokens: string[]) => {
    const m = leftTokens.length;
    const n = rightTokens.length;
    const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
    for (let i = m - 1; i >= 0; i -= 1) {
      for (let j = n - 1; j >= 0; j -= 1) {
        if (leftTokens[i] === rightTokens[j]) {
          dp[i][j] = dp[i + 1][j + 1] + 1;
        } else {
          dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
        }
      }
    }
    const leftSpans: Array<{ text: string; type: 'equal' | 'del' }> = [];
    const rightSpans: Array<{ text: string; type: 'equal' | 'add' }> = [];
    let i = 0;
    let j = 0;
    while (i < m && j < n) {
      if (leftTokens[i] === rightTokens[j]) {
        leftSpans.push({ text: leftTokens[i], type: 'equal' });
        rightSpans.push({ text: rightTokens[j], type: 'equal' });
        i += 1;
        j += 1;
      } else if (dp[i + 1][j] >= dp[i][j + 1]) {
        leftSpans.push({ text: leftTokens[i], type: 'del' });
        i += 1;
      } else {
        rightSpans.push({ text: rightTokens[j], type: 'add' });
        j += 1;
      }
    }
    while (i < m) {
      leftSpans.push({ text: leftTokens[i], type: 'del' });
      i += 1;
    }
    while (j < n) {
      rightSpans.push({ text: rightTokens[j], type: 'add' });
      j += 1;
    }
    return { leftSpans, rightSpans };
  }, []);

  const renderWordDiff = useCallback(
    (leftLine: string, rightLine: string) => {
      const leftTokens = tokenize(leftLine.replace(/^[-+]/, ''));
      const rightTokens = tokenize(rightLine.replace(/^[-+]/, ''));
      const { leftSpans, rightSpans } = diffTokens(leftTokens, rightTokens);
      return {
        left: leftSpans.map((span, index) => (
          <span
            key={`left-${index}`}
            className={span.type === 'del' ? 'running-config__diff-token running-config__diff-token--removed' : undefined}
          >
            {span.text}
          </span>
        )),
        right: rightSpans.map((span, index) => (
          <span
            key={`right-${index}`}
            className={span.type === 'add' ? 'running-config__diff-token running-config__diff-token--added' : undefined}
          >
            {span.text}
          </span>
        ))
      };
    },
    [diffTokens, tokenize]
  );

  const renderSideBySide = useCallback(
    (text: string, wordLevel: boolean) => {
      const rows = buildSideBySideRows(text);
      return (
        <div className="running-config__diff-grid">
          {rows.map((row, index) => {
            if (row.meta) {
              return (
                <div key={`meta-${index}`} className="running-config__diff-row running-config__diff-row--meta">
                  <div className="running-config__diff-cell running-config__diff-cell--meta">{row.meta}</div>
                </div>
              );
            }
            const left = row.left ?? '';
            const right = row.right ?? '';
            const leftClass =
              row.leftType === 'del' ? 'running-config__diff-cell running-config__diff-cell--removed' : 'running-config__diff-cell';
            const rightClass =
              row.rightType === 'add' ? 'running-config__diff-cell running-config__diff-cell--added' : 'running-config__diff-cell';
            if (wordLevel && row.leftType === 'del' && row.rightType === 'add') {
              const tokens = renderWordDiff(left, right);
              return (
                <div key={`row-${index}`} className="running-config__diff-row">
                  <div className={leftClass}>{tokens.left}</div>
                  <div className={rightClass}>{tokens.right}</div>
                </div>
              );
            }
            return (
              <div key={`row-${index}`} className="running-config__diff-row">
                <div className={leftClass}>{left || ' '}</div>
                <div className={rightClass}>{right || ' '}</div>
              </div>
            );
          })}
        </div>
      );
    },
    [buildSideBySideRows, renderWordDiff]
  );

  return (
    <OrgModuleLayout
      title="Running config"
      description={
        <>
          Running configuration snapshots for{' '}
          {device?.displayName ? (
            <>Device <strong>{device.displayName}</strong></>
          ) : device?.hostname ? (
            <>Device <strong>{device.hostname}</strong></>
          ) : deviceId ? (
            <>Device <strong>{deviceId}</strong></>
          ) : (
            'this device'
          )}
          .
        </>
      }
      actions={
        <div className="button-row">
          <button type="button" className="ui-button ui-button--ghost" onClick={() => navigate(returnToInventory)}>
            Back to inventory
          </button>
        </div>
      }
    >
      {!orgId || !deviceId ? (
        <OrgModulePlaceholder intent="Select a device to view its running config." />
      ) : (
        <div className="module-panel">
          {error && <p className="form-error">{error}</p>}
          <section className="section-card">
            <header className="section-card__header">
              <div>
                <h3 className="section-card__title">Current Running Config Summary</h3>
              </div>
              {effectiveLatest?.startupConfigInSync === false ? (
                <div className="button-row">
                  <button type="button" className="ui-button ui-button--ghost" onClick={() => void handleLoadStartupDiff()}>
                    <ForkIcon size={16} />
                    <span>View startup drift</span>
                  </button>
                </div>
              ) : null}
            </header>
            {loading ? (
              <p>Loading summary…</p>
            ) : latestConfigSummary || enabledFeatures.length > 0 ? (
              <div className="running-config__summary-stack">
                {latestConfigSummary && (
                  <ul className="running-config__summary">
                    {latestConfigSummary.map((item, index) => (
                      <li key={`latest-summary-${index}`}>{item}</li>
                    ))}
                  </ul>
                )}
                {enabledFeatures.length > 0 && (
                  <div className="running-config__feature-box">
                    <p className="running-config__feature-box-title">Enabled features</p>
                    <ul className="running-config__feature-list">
                      {enabledFeatures.map((feature) => (
                        <li key={feature}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <p className="helper-text">No running config summary available yet.</p>
            )}
          </section>

          {/* Scoped CSS Style Injection for CVE styles */}
          <style dangerouslySetInnerHTML={{ __html: `
            .device-cve-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
              gap: 1rem;
              margin-top: 1rem;
            }
            .device-cve-card {
              background: var(--surface-panel-muted);
              border: 1px solid var(--border-subtle);
              border-radius: 8px;
              padding: 1rem;
              display: flex;
              flex-direction: column;
              gap: 0.75rem;
              cursor: pointer;
              transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
            }
            .device-cve-card:hover {
              transform: translateY(-2px);
              box-shadow: 0 4px 12px rgba(var(--brand-ink-rgb), 0.05);
              border-color: var(--brand-primary);
            }
            .device-cve-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              gap: 0.5rem;
            }
            .device-cve-title {
              font-family: var(--font-mono), monospace;
              font-size: 0.9rem;
              font-weight: 700;
              color: var(--text-primary);
            }
            .device-cve-badge-group {
              display: flex;
              gap: 0.5rem;
              align-items: center;
            }
            .device-cve-justification {
              font-size: 0.78rem;
              color: var(--text-secondary);
              line-height: 1.4;
              display: -webkit-box;
              -webkit-line-clamp: 2;
              -webkit-box-orient: vertical;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .device-cve-remediation {
              font-size: 0.75rem;
              font-weight: 600;
              margin-top: auto;
              padding-top: 0.5rem;
              border-top: 1px dashed var(--border-subtle);
              display: flex;
              align-items: center;
              gap: 0.25rem;
            }
            /* Severity and Status Badge Styles */
            .cve-badge-severity {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              gap: 0.25rem;
              font-weight: 700;
              font-size: 0.65rem;
              letter-spacing: 0.02em;
              padding: 0.15rem 0.45rem;
              border-radius: 4px;
              line-height: 1;
            }
            .sev-critical {
              background-color: rgba(239, 68, 68, 0.1);
              color: hsl(354, 85%, 48%);
              border: 1px solid rgba(239, 68, 68, 0.15);
            }
            .sev-high {
              background-color: rgba(245, 158, 11, 0.1);
              color: hsl(25, 90%, 45%);
              border: 1px solid rgba(245, 158, 11, 0.15);
            }
            .sev-medium {
              background-color: rgba(59, 130, 246, 0.1);
              color: hsl(210, 85%, 45%);
              border: 1px solid rgba(59, 130, 246, 0.15);
            }
            .sev-low {
              background-color: rgba(107, 114, 128, 0.1);
              color: var(--brand-gray-600);
              border: 1px solid rgba(107, 114, 128, 0.15);
            }
            .cve-badge-status {
              display: inline-block;
              font-weight: 800;
              font-size: 0.62rem;
              letter-spacing: 0.04em;
              padding: 0.15rem 0.4rem;
              border-radius: 4px;
              line-height: 1;
            }
            .status-exposed {
              background-color: rgba(239, 68, 68, 0.15);
              color: hsl(354, 85%, 45%);
              border: 1px solid rgba(239, 68, 68, 0.2);
            }
            .status-safe {
              background-color: rgba(16, 185, 129, 0.12);
              color: hsl(142, 70%, 35%);
              border: 1px solid rgba(16, 185, 129, 0.18);
            }
            .cve-remediation-type {
              font-size: 0.72rem;
              font-weight: 600;
            }
            .cve-remediation-type.type-upgrade {
              color: var(--brand-primary);
            }
            .cve-remediation-type.type-config {
              color: hsl(25, 90%, 45%);
            }
            .cve-drawer-details {
              display: flex;
              flex-direction: column;
              gap: 1.25rem;
              padding: 0.5rem 0;
            }
            .cve-drawer-header-meta {
              display: flex;
              align-items: center;
              gap: 1rem;
              flex-wrap: wrap;
              background: var(--surface-panel-muted);
              border: 1px solid var(--border-subtle);
              border-radius: 8px;
              padding: 0.75rem 1rem;
            }
            .cve-meta-item {
              display: flex;
              flex-direction: column;
              gap: 0.2rem;
            }
            .cve-meta-item span {
              font-size: 0.65rem;
              font-weight: 600;
              color: var(--text-secondary);
              text-transform: uppercase;
            }
            .cve-meta-item strong {
              font-size: 0.85rem;
              color: var(--text-primary);
            }
            .cve-drawer-section {
              display: flex;
              flex-direction: column;
              gap: 0.5rem;
            }
            .cve-section-title {
              font-size: 0.78rem;
              font-weight: 700;
              text-transform: uppercase;
              color: var(--text-secondary);
              letter-spacing: 0.04em;
              border-bottom: 1px solid var(--border-subtle);
              padding-bottom: 0.35rem;
            }
            .cve-justification-text {
              font-size: 0.85rem;
              line-height: 1.45;
              color: var(--text-primary);
              background: var(--brand-meridian-10);
              border: 1px solid var(--brand-meridian-100);
              border-radius: 6px;
              padding: 0.75rem;
            }
            .cve-console {
              background: var(--terminal-bg);
              border: 1px solid var(--terminal-border);
              border-radius: 8px;
              padding: 0.75rem;
              font-family: var(--font-mono), monospace;
              color: var(--terminal-fg);
              font-size: 0.78rem;
              line-height: 1.4;
              max-height: 200px;
              overflow-y: auto;
              white-space: pre-wrap;
              word-break: break-all;
            }
            .cve-console-line {
              display: flex;
              gap: 0.75rem;
            }
            .cve-console-num {
              color: var(--text-secondary);
              text-align: right;
              width: 24px;
              user-select: none;
              opacity: 0.5;
            }
            .cve-console-code {
              color: var(--terminal-fg);
              flex: 1;
            }
            .cve-console-match {
              background: var(--terminal-highlight);
              font-weight: 600;
              border-left: 2px solid var(--brand-accent);
              padding-left: 2px;
            }
            .cve-command-box {
              display: flex;
              flex-direction: column;
              gap: 0.5rem;
            }
            .cve-command-row {
              display: flex;
              align-items: center;
              justify-content: space-between;
              background: var(--brand-gray-10);
              border: 1px solid var(--border-subtle);
              border-radius: 6px;
              padding: 0.5rem 0.75rem;
              gap: 0.75rem;
            }
            .cve-command-text {
              font-family: var(--font-mono), monospace;
              font-size: 0.78rem;
              color: var(--text-primary);
              word-break: break-all;
              flex: 1;
            }
            .cve-btn-copy {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              border: 1px solid var(--border-subtle);
              background: var(--surface-panel);
              border-radius: 4px;
              padding: 0.2rem 0.4rem;
              font-size: 0.68rem;
              font-weight: 700;
              color: var(--text-secondary);
              cursor: pointer;
              transition: all 0.15s ease;
            }
            .cve-btn-copy:hover {
              background: var(--brand-primary-soft);
              color: var(--brand-primary);
              border-color: var(--brand-primary);
            }
            .cve-btn-copy.copied {
              background: rgba(16, 185, 129, 0.1);
              color: hsl(142, 70%, 35%);
              border-color: hsl(142, 70%, 35%);
            }
            .cve-impact-box {
              font-size: 0.82rem;
              line-height: 1.4;
              color: hsl(25, 90%, 40%);
              background: rgba(245, 158, 11, 0.08);
              border: 1px solid rgba(245, 158, 11, 0.18);
              border-radius: 6px;
              padding: 0.75rem;
              font-style: italic;
            }
            .cve-impact-box-container {
              display: flex;
              flex-direction: column;
              gap: 0.5rem;
              margin-top: 0.25rem;
            }
            .cve-plane-badge {
              align-self: flex-start;
              font-size: 0.68rem;
              font-weight: 700;
              letter-spacing: 0.04em;
              text-transform: uppercase;
              padding: 0.15rem 0.45rem;
              border-radius: 4px;
              display: inline-flex;
              align-items: center;
              box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
            }
            .cve-plane-management {
              background: hsla(210, 100%, 50%, 0.1);
              color: hsl(210, 100%, 65%);
              border: 1px solid hsla(210, 100%, 50%, 0.3);
            }
            .cve-plane-control {
              background: hsla(280, 100%, 50%, 0.1);
              color: hsl(280, 100%, 65%);
              border: 1px solid hsla(280, 100%, 50%, 0.3);
            }
            .cve-plane-data {
              background: hsla(340, 100%, 50%, 0.1);
              color: hsl(340, 100%, 65%);
              border: 1px solid hsla(340, 100%, 50%, 0.3);
            }
            .cve-remediation-box-upgrade {
              background: var(--brand-meridian-10);
              border: 1px solid var(--brand-meridian-100);
              border-radius: 6px;
              padding: 0.75rem;
              font-size: 0.85rem;
              line-height: 1.45;
              color: var(--text-primary);
            }
          ` }} />

          <section className="section-card">
            <header className="section-card__header">
              <div>
                <h3 className="section-card__title">CVE Vulnerability Assessments</h3>
              </div>
            </header>
            {assessmentsLoading ? (
              <p>Loading assessments…</p>
            ) : assessments.length === 0 ? (
              <p className="helper-text">No vulnerability assessments found for this device.</p>
            ) : (
              <div className="device-cve-grid">
                {assessments.map((item) => {
                  const isExposed = item.status === 'EXPOSED';
                  const isUpgrade = item.remediationType === 'UPGRADE';
                  const sev = item.severity.toUpperCase();
                  return (
                    <div
                      key={item.id || item.cveId}
                      className="device-cve-card"
                      onClick={() => {
                        setSelectedExposure(item);
                        setCopiedIndex(null);
                      }}
                    >
                      <div className="device-cve-header">
                        <span className="device-cve-title">{item.cveId}</span>
                        <div className="device-cve-badge-group">
                          <span className={`cve-badge-severity sev-${sev.toLowerCase()}`}>
                            {sev}
                            {item.cvssScore !== undefined && <span className="cve-cvss-score">({item.cvssScore.toFixed(1)})</span>}
                          </span>
                          <span className={`cve-badge-status ${isExposed ? 'status-exposed' : 'status-safe'}`}>
                            {isExposed ? 'EXPOSED' : 'AUDITED SAFE'}
                          </span>
                        </div>
                      </div>
                      <p className="device-cve-justification">
                        {item.justification || 'No justification details provided.'}
                      </p>
                      {isExposed && (
                        <div className="device-cve-remediation">
                          <span className={`cve-remediation-type type-${isUpgrade ? 'upgrade' : 'config'}`}>
                            {isUpgrade ? '🔀 Upgrade recommended' : '🔧 Workaround available'}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section className="section-card">
            <header className="section-card__header">
              <div>
                <h3 className="section-card__title">Change history</h3>
              </div>
            </header>
            {loading ? (
              <p>Loading history…</p>
            ) : historyRows.length === 0 ? (
              <p className="helper-text">No snapshots yet to show.</p>
            ) : (
              <>
                <div className="running-config__history">
                  {historyRows.map((row) => {
                    const percent = Math.max(8, Math.round((row.changeValue / maxChangeValue) * 100));
                    const isActive = activeSnapshotId === row.version.id;
                    const isLatest = effectiveLatest?.id === row.version.id;
                    const startupDiffers = isLatest && effectiveLatest?.startupConfigInSync === false;
                    return (
                      <div
                        key={row.version.id}
                        className={`running-config__history-row${isActive ? ' running-config__history-row--active' : ''}`}
                      >
                        <div
                          className="running-config__history-main"
                          role="button"
                          tabIndex={0}
                          onClick={() => handleSelectSnapshot(row.version.id)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              handleSelectSnapshot(row.version.id);
                            }
                          }}
                        >
                          <div className="running-config__history-title">
                            <span>{formatDateTime(row.version.collectedAt)}</span>
                            {isLatest && <span className="running-config__latest-pill">Latest</span>}
                            {startupDiffers && (
                              <button
                                type="button"
                                className="running-config__history-ribbon running-config__history-ribbon--drift"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  void handleLoadStartupDiff();
                                }}
                              >
                                <ForkIcon size={14} />
                                <span>Startup drift</span>
                              </button>
                            )}
                          </div>
                          <div className="running-config__history-meta">
                            <div className="running-config__history-bar">
                              <div className="running-config__history-bar-fill" style={{ width: `${percent}%` }} />
                            </div>
                            <span className="running-config__history-size">{row.changeLabel}</span>
                          </div>
                          <p className="running-config__history-subtitle">
                            {row.version.diffSummary?.[0] ?? 'No summary available.'}
                          </p>
                        </div>
                        <div className="running-config__history-actions">
                          <button
                            type="button"
                            className="list-workspace__icon-button"
                            aria-label="View snapshot"
                            title="View snapshot"
                            onClick={() => handleShowSnapshot(row.version.id)}
                          >
                            <EyeIcon size={16} />
                          </button>
                        </div>
                        {row.version.configSummary?.length ? (
                          <div className="running-config__history-tooltip" role="tooltip">
                            <p className="running-config__history-tooltip-title">Snapshot summary</p>
                            <ul className="running-config__summary">
                              {(row.version.configSummary ?? []).map((item, index) => (
                                <li key={`${row.version.id}-summary-${index}`}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
                <div className="running-config__history-footer">
                  <button
                    type="button"
                    className="ui-button ui-button--ghost"
                    onClick={() => setVersionsLimit(showMoreActive ? 5 : 20)}
                  >
                    {showMoreActive ? 'Show fewer' : 'Show more'}
                  </button>
                </div>
              </>
            )}
          </section>

          <section className="section-card">
            <header className="section-card__header">
              <div>
                <h3 className="section-card__title">Diff Between Snapshots</h3>
              </div>
              <div className="button-row">
                <button
                  type="button"
                  className="ui-button ui-button--ghost"
                  onClick={() => displayDiffText && copyToClipboard(displayDiffText, 'Diff')}
                  disabled={!displayDiffText}
                  aria-label="Copy diff"
                  title="Copy diff"
                >
                  <CopyIcon size={16} />
                </button>
                <button
                  type="button"
                  className="ui-button ui-button--ghost"
                  onClick={() => displayDiffText && downloadText(displayDiffText, diffFilename)}
                  disabled={!displayDiffText}
                  aria-label="Download diff"
                  title="Download diff"
                >
                  <DownloadIcon size={16} />
                </button>
              </div>
            </header>
            {loading ? (
              <p>Loading versions…</p>
            ) : effectiveVersions.length < 2 ? (
              <p className="helper-text">Not enough snapshots to show a diff yet.</p>
            ) : (
              <>
                <div className="form-grid form-grid--two">
                  <label className="form-field">
                    <span className="form-field__label">From</span>
                    <select
                      className="select"
                      value={selectedFrom}
                      onChange={(event) => setSelectedFrom(event.target.value)}
                    >
                      {effectiveVersions.map((version) => (
                        <option key={version.id} value={version.id}>
                          {formatDateTime(version.collectedAt)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="form-field">
                    <span className="form-field__label">To</span>
                    <select className="select" value={selectedTo} onChange={(event) => setSelectedTo(event.target.value)}>
                      {effectiveVersions.map((version) => (
                        <option key={version.id} value={version.id}>
                          {formatDateTime(version.collectedAt)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="form-field">
                    <span className="form-field__label">Diff style</span>
                    <select className="select" value={diffMode} onChange={(event) => setDiffMode(event.target.value as typeof diffMode)}>
                      <option value="unified">Unified (git diff)</option>
                      <option value="side-by-side">Side-by-side</option>
                      <option value="word">Word-level</option>
                    </select>
                  </label>
                </div>
                {diffLoading ? (
                  <p>Loading diff…</p>
                ) : displayDiffText ? (
                  <div className="chat-inspector__pre-wrap running-config__diff-wrap">
                    {effectiveStartupDiffText && <p className="helper-text">Startup vs running diff (latest).</p>}
                    {diffMode === 'unified' && <div className="running-config__diff">{renderUnifiedDiff(displayDiffText)}</div>}
                    {diffMode === 'side-by-side' && renderSideBySide(displayDiffText, false)}
                    {diffMode === 'word' && renderSideBySide(displayDiffText, true)}
                  </div>
                ) : (
                  <p className="helper-text">Select two snapshots to view differences.</p>
                )}
              </>
            )}
          </section>
        </div>
      )}
      <Drawer
        open={startupDiffDrawerOpen}
        title="Startup vs Running Diff"
        subtitle={latest?.collectedAt ? formatDateTime(latest.collectedAt) : undefined}
        onClose={() => {
          setStartupDiffDrawerOpen(false);
          setStartupDiffText(null);
          setStartupConfigText(null);
        }}
        width="wide"
        actions={
          startupDiffText ? (
            <div className="running-config__drawer-action-groups">
              <div className="running-config__drawer-action-group">
                <span className="running-config__drawer-action-label">Diff</span>
                <button
                  type="button"
                  className="drawer-icon-button"
                  aria-label="Copy startup diff"
                  title="Copy startup diff"
                  onClick={() => copyToClipboard(startupDiffText, 'Startup diff')}
                >
                  <CopyIcon size={18} />
                </button>
                <button
                  type="button"
                  className="drawer-icon-button"
                  aria-label="Download startup diff"
                  title="Download startup diff"
                  onClick={() => startupDiffText && downloadText(startupDiffText, startupDiffFilename)}
                >
                  <DownloadIcon size={18} />
                </button>
              </div>
              <div className="running-config__drawer-action-group">
                <span className="running-config__drawer-action-label">Startup-config</span>
                <button
                  type="button"
                  className="drawer-icon-button"
                  aria-label="Copy startup config"
                  title="Copy startup config"
                  onClick={() => startupConfigText && copyToClipboard(startupConfigText, 'Startup config')}
                  disabled={!startupConfigText}
                >
                  <CopyIcon size={18} />
                </button>
                <button
                  type="button"
                  className="drawer-icon-button"
                  aria-label="Download startup config"
                  title="Download startup config"
                  onClick={() => startupConfigText && downloadText(startupConfigText, startupConfigFilename)}
                  disabled={!startupConfigText}
                >
                  <DownloadIcon size={18} />
                </button>
              </div>
            </div>
          ) : undefined
        }
      >
        {startupDiffLoading ? (
          <p>Loading startup diff…</p>
        ) : startupDiffText ? (
          <div className="chat-inspector__pre-wrap running-config__diff-wrap">
            {diffMode === 'unified' && <div className="running-config__diff">{renderUnifiedDiff(startupDiffText)}</div>}
            {diffMode === 'side-by-side' && renderSideBySide(startupDiffText, false)}
            {diffMode === 'word' && renderSideBySide(startupDiffText, true)}
          </div>
        ) : (
          <OrgModulePlaceholder intent="Startup diff not available." />
        )}
      </Drawer>
      <Drawer
        open={Boolean(snapshotDrawerId)}
        title="Running config snapshot"
        subtitle={drawerVersion?.collectedAt ? formatDateTime(drawerVersion.collectedAt) : snapshotDrawerId ?? undefined}
        onClose={() => {
          setSnapshotDrawerId(null);
          setSnapshotDrawerSnapshot(null);
        }}
        width="wide"
        actions={
          snapshotDrawerSnapshot?.text ? (
            <div className="drawer-icon-actions">
              <button
                type="button"
                className="drawer-icon-button"
                aria-label="Copy running config"
                title="Copy running config"
                onClick={() => copyToClipboard(snapshotDrawerSnapshot.text ?? '', 'Running config')}
              >
                <CopyIcon size={18} />
              </button>
              <button
                type="button"
                className="drawer-icon-button"
                aria-label="Download running config"
                title="Download running config"
                onClick={() => snapshotDrawerSnapshot.text && drawerFilename && downloadText(snapshotDrawerSnapshot.text, drawerFilename)}
                disabled={!drawerFilename}
              >
                <DownloadIcon size={18} />
              </button>
            </div>
          ) : undefined
        }
      >
        {snapshotDrawerLoading ? (
          <p>Loading snapshot…</p>
        ) : snapshotDrawerSnapshot?.text ? (
          <div className="device-drawer">
            {drawerConfigSummary && (
              <section className="detail-section">
                <header>
                  <h3>Summary</h3>
                </header>
                <ul className="running-config__summary">
                  {drawerConfigSummary.map((item, index) => (
                    <li key={`drawer-summary-${index}`}>{item}</li>
                  ))}
                </ul>
              </section>
            )}
            <section className="detail-section">
              <header>
                <h3>Running config</h3>
              </header>
              <div className="chat-inspector__pre-wrap">
                <pre className="chat-inspector__pre chat-inspector__pre--terminal chat-inspector__pre--wrap">
                  {snapshotDrawerSnapshot.text}
                </pre>
              </div>
            </section>
          </div>
        ) : (
          <OrgModulePlaceholder intent="Snapshot not available." />
        )}
      </Drawer>
      <Drawer
        open={Boolean(selectedExposure)}
        title={`Vulnerability Assessment: ${selectedExposure?.cveId}`}
        subtitle={selectedExposure?.deviceName ? `Device: ${selectedExposure.deviceName}` : undefined}
        onClose={() => {
          setSelectedExposure(null);
          setCopiedIndex(null);
        }}
        width="wide"
      >
        {selectedExposure ? (
          <div className="cve-drawer-details">
            <div className="cve-drawer-header-meta">
              <div className="cve-meta-item">
                <span>CVE Severity</span>
                <span className={`cve-badge-severity sev-${selectedExposure.severity.toLowerCase()}`}>
                  {selectedExposure.severity}
                  {selectedExposure.cvssScore !== undefined && ` (${selectedExposure.cvssScore.toFixed(1)})`}
                </span>
              </div>
              <div className="cve-meta-item">
                <span>Exposure Status</span>
                <span className={`cve-badge-status ${selectedExposure.status === 'EXPOSED' ? 'status-exposed' : 'status-safe'}`}>
                  {selectedExposure.status === 'EXPOSED' ? 'EXPOSED' : 'AUDITED SAFE'}
                </span>
              </div>
              {selectedExposure.assessedAt && (
                <div className="cve-meta-item">
                  <span>Assessed At</span>
                  <strong>{formatDateTime(selectedExposure.assessedAt)}</strong>
                </div>
              )}
            </div>

            <div className="cve-drawer-section">
              <span className="cve-section-title">Audit Justification & Reasoning</span>
              <div className="cve-justification-text">
                {selectedExposure.justification}
              </div>
            </div>

            {selectedExposure.causativeConfig && (
              <div className="cve-drawer-section">
                <span className="cve-section-title">
                  {selectedExposure.status === 'EXPOSED'
                    ? 'Causative Running Configuration Lines'
                    : 'Configuration Audit Evidence (Required Patterns Absent/Negated)'}
                </span>
                <div className="cve-console">
                  {selectedExposure.causativeConfig.split('\n').map((line, idx) => (
                    <div key={idx} className="cve-console-line">
                      <span className="cve-console-num">{idx + 1}</span>
                      <span className={`cve-console-code ${line.trim() ? 'cve-console-match' : ''}`}>
                        {line}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedExposure.status === 'EXPOSED' && (
              <div className="cve-drawer-section">
                <span className="cve-section-title">Remediation Action</span>
                <div className="cve-remediation-box-upgrade" style={{ marginBottom: selectedExposure.remediationCommands && selectedExposure.remediationCommands.length > 0 ? '1rem' : '0' }}>
                  🔀 <strong>Primary Recommendation: Firmware / Software Upgrade</strong>
                  <br />
                  <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                    To completely resolve this vulnerability, upgrade the device to the recommended secure software image release.
                  </span>
                  <div style={{ marginTop: '0.5rem' }}>
                    Target patch release: <strong>{selectedExposure.patchUpgrade || 'Latest secure release'}</strong>
                  </div>
                </div>

                {selectedExposure.remediationCommands && selectedExposure.remediationCommands.length > 0 && (
                  <div className="cve-command-box">
                    <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: '0 0 0.5rem 0' }}>
                      🔧 <strong>Configuration Workaround / Mitigation (Non-disruptive)</strong>
                    </p>
                    <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: '0 0 0.5rem 0' }}>
                      If an immediate software upgrade is not feasible, apply the following CLI commands to mitigate the active exposure:
                    </p>
                    {selectedExposure.remediationCommands.map((cmd, idx) => (
                      <div key={idx} className="cve-command-row">
                        <span className="cve-command-text">{cmd}</span>
                        <button
                          type="button"
                          className={`cve-btn-copy ${copiedIndex === idx ? 'copied' : ''}`}
                          onClick={() => {
                            navigator.clipboard.writeText(cmd);
                            setCopiedIndex(idx);
                            setTimeout(() => setCopiedIndex(p => p === idx ? null : p), 2000);
                          }}
                        >
                          {copiedIndex === idx ? 'Copied! ✓' : 'Copy'}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {selectedExposure.status === 'EXPOSED' && selectedExposure.impactOfFix && (() => {
              const rawImpact = selectedExposure.impactOfFix;
              let planeType = '';
              let impactText = rawImpact;
              
              if (rawImpact.startsWith('Management Plane Impact:')) {
                planeType = 'Management Plane';
                impactText = rawImpact.substring('Management Plane Impact:'.length).trim();
              } else if (rawImpact.startsWith('Control Plane Impact:')) {
                planeType = 'Control Plane';
                impactText = rawImpact.substring('Control Plane Impact:'.length).trim();
              } else if (rawImpact.startsWith('Data Plane Impact:')) {
                planeType = 'Data Plane';
                impactText = rawImpact.substring('Data Plane Impact:'.length).trim();
              }
              
              return (
                <div className="cve-drawer-section">
                  <span className="cve-section-title">Operational Impact Statement</span>
                  <div className="cve-impact-box-container">
                    {planeType && (
                      <span className={`cve-plane-badge cve-plane-${planeType.split(' ')[0].toLowerCase()}`}>
                        {planeType}
                      </span>
                    )}
                    <div className="cve-impact-box">
                      ⚠️ {impactText}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        ) : null}
      </Drawer>
    </OrgModuleLayout>
  );
};

export default DeviceRunningConfigPage;
